import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { CategoryGroupFormUtils } from '../../utils/productCategoryGroupCommon';
import { getCategoryGroupDetail, getProductCountByTreeCodes, CategoryGroupDetail, CategoryGroupTreeNode as ApiCategoryGroupTreeNode, getTreeProducts, updateProductInTree } from '../../utils/productCategoryGroup';
import { getProductList, Product, ProductListRequest } from '../../utils/product';
import { generateAfter, generateBetween } from '../../utils/fractionalIndex';
import { 
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  X,
  Package,
  FolderTree,
  GripVertical
} from 'lucide-react';


// 카테고리/그룹 트리 노드 인터페이스 (UI용)
interface CategoryGroupNode {
  code: string;
  title: string;
  brandCode?: string;
  parentCode?: string;
  depth: number;
  sortOrder?: number;
  status?: string;
  type?: string;
  children?: CategoryGroupNode[];
}

// API 트리 노드(sub) -> UI 트리 노드(children) 변환
const convertApiTreeToNodes = (nodes: ApiCategoryGroupTreeNode[] | undefined): CategoryGroupNode[] => {
  if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return [];
  return nodes.map((node, index) => ({
    code: node.code,
    title: node.title,
    depth: node.depth ?? 0,
    status: node.status,
    sortOrder: index,
    children: node.sub && node.sub.length > 0 ? convertApiTreeToNodes(node.sub) : undefined
  }));
};

// 카테고리/그룹 상품 매핑 (로컬 상태, sortOrder는 fractional index)
interface CategoryGroupProduct {
  categoryGroupCode: string;
  productCode: string;
  order: number;
  sortOrder: string;
}

const CategoryGroupProductConfig: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryGroupCode } = useParams<{ categoryGroupCode: string }>();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-category-group-list');

  // 카테고리/그룹 트리 상태
  const [treeData, setTreeData] = useState<CategoryGroupNode[]>([]);
  const [selectedCategoryGroupCode, setSelectedCategoryGroupCode] = useState<string | null>(null);
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [treeNodeProductCounts, setTreeNodeProductCounts] = useState<Record<string, number>>({});
  const [treeCountsLoaded, setTreeCountsLoaded] = useState(false);

  // 상품 상태
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productListError, setProductListError] = useState<string | null>(null);

  // 드래그앤드랍 상태
  const [draggedProduct, setDraggedProduct] = useState<Product | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);
  const [dropInsertIndex, setDropInsertIndex] = useState<number | null>(null);

  // 카테고리/그룹별 상품 목록 (노드 선택 시 API 로드, 드롭·삭제 시 즉시 API 호출로 반영)
  const [categoryGroupProducts, setCategoryGroupProducts] = useState<Map<string, CategoryGroupProduct[]>>(new Map());
  const [isLoadingNodeProducts, setIsLoadingNodeProducts] = useState(false);
  const [apiBusy, setApiBusy] = useState(false);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [selectedCategoryGroup, setSelectedCategoryGroup] = useState<CategoryGroupDetail | null>(null);

  // Check if screen is mobile size
  CategoryGroupFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = CategoryGroupFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 카테고리/그룹 상세 정보 및 트리 데이터 로드 (한 번의 API 호출로 통합)
  useEffect(() => {
    const fetchCategoryGroupData = async () => {
      if (!projectDetail || !categoryGroupCode) {
        return;
      }

      try {
        setIsLoadingTree(true);
        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) {
          return;
        }

        const categoryGroupDetail = await getCategoryGroupDetail(projectCode, categoryGroupCode);
        setSelectedCategoryGroup(categoryGroupDetail);

        // tree 필드에서 트리 노드 변환 (API sub -> UI children)
        const initialTreeData = convertApiTreeToNodes(categoryGroupDetail.tree);
        setTreeData(initialTreeData);

        // 모든 노드 펼치기
        const collectAllCodes = (nodes: CategoryGroupNode[]): string[] => {
          const codes: string[] = [];
          nodes.forEach(node => {
            codes.push(node.code);
            if (node.children && node.children.length > 0) {
              codes.push(...collectAllCodes(node.children));
            }
          });
          return codes;
        };
        setExpandedCodes(new Set(collectAllCodes(initialTreeData)));

        setTreeCountsLoaded(false);
        const nodeCodes = collectAllCodes(initialTreeData);
        if (nodeCodes.length > 0) {
          const treeCodes = nodeCodes.map((code) => `${categoryGroupCode}:${code}`);
          try {
            const counts = await getProductCountByTreeCodes(projectCode, treeCodes);
            setTreeNodeProductCounts(counts);
          } catch (countErr) {
            console.error('노드별 상품 수 조회 오류:', countErr);
            setTreeNodeProductCounts({});
          }
        } else {
          setTreeNodeProductCounts({});
        }
        setTreeCountsLoaded(true);
      } catch (err) {
        console.error('카테고리/그룹 정보 조회 오류:', err);
        setSelectedCategoryGroup({
          code: categoryGroupCode,
          title: categoryGroupCode,
          status: 'ACTIVE',
          type: 'CATEGORY'
        } as CategoryGroupDetail);
        setTreeData([]);
        setTreeNodeProductCounts({});
      } finally {
        setTreeCountsLoaded(true);
        setIsLoadingTree(false);
      }
    };

    if (projectDetail) {
      fetchCategoryGroupData();
    }
  }, [projectDetail, categoryGroupCode]);

  // 전체 상품 목록 조회 (현재 카테고리/그룹 상세 로드 후, 해당 브랜드로 필터)
  useEffect(() => {
    const fetchProducts = async () => {
      if (!projectDetail || !categoryGroupCode || selectedCategoryGroup == null) {
        return;
      }

      try {
        setIsLoadingProducts(true);
        setProductListError(null);

        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }

        const request: ProductListRequest = {
          page: 0,
          size: 1000,
          ...(selectedCategoryGroup.brandCode && { brandCode: selectedCategoryGroup.brandCode })
        };

        const response = await getProductList(projectCode, request);
        setAllProducts(response.products || []);
      } catch (err) {
        console.error('상품 목록 조회 오류:', err);
        setProductListError(err instanceof Error ? err.message : '상품 목록을 불러오는 중 오류가 발생했습니다.');
        setAllProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [projectDetail, categoryGroupCode, selectedCategoryGroup]);

  // 선택한 노드의 상품 목록 조회 (tree-products API)
  useEffect(() => {
    const fetchNodeProducts = async () => {
      if (!projectDetail || !categoryGroupCode || !selectedCategoryGroupCode) {
        return;
      }

      const projectCode = projectDetail.code || getProjectCode();
      if (!projectCode) return;

      const treeCode = `${categoryGroupCode}:${selectedCategoryGroupCode}`;

      try {
        setIsLoadingNodeProducts(true);
        const res = await getTreeProducts(projectCode, [treeCode]);
        console.log('[tree-products] 요청 treeCode:', treeCode, '응답:', res);
        const list: CategoryGroupProduct[] = (res.products || []).map((item, index) => ({
          categoryGroupCode: selectedCategoryGroupCode,
          productCode: item.productCode,
          order: index,
          sortOrder: item.sortOrder ?? ''
        }));
        setCategoryGroupProducts(prev => new Map(prev).set(selectedCategoryGroupCode, list));
      } catch (err) {
        console.error('노드 상품 목록 조회 오류:', err);
        setCategoryGroupProducts(prev => new Map(prev).set(selectedCategoryGroupCode, []));
      } finally {
        setIsLoadingNodeProducts(false);
      }
    };

    fetchNodeProducts();
  }, [projectDetail, categoryGroupCode, selectedCategoryGroupCode]);

  // 카테고리/그룹 토글
  const handleCategoryGroupToggle = (code: string) => {
    setExpandedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  // 카테고리/그룹 선택
  const handleCategoryGroupSelect = (code: string) => {
    setSelectedCategoryGroupCode(code);
  };

  // 드래그 시작 (오른쪽에서)
  const handleDragStartFromAll = (product: Product) => (e: React.DragEvent) => {
    setDraggedProduct(product);
    setDraggedIndex(null);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // 드래그 시작 (가운데에서 - 순서 변경용)
  const handleDragStartFromCategory = (product: Product, index: number) => (e: React.DragEvent) => {
    setDraggedProduct(product);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // 드래그 종료
  const handleDragEnd = () => {
    setDraggedProduct(null);
    setDraggedIndex(null);
    setDropZoneActive(false);
    setDropInsertIndex(null);
  };

  // treeCode 생성: 그룹코드:노드코드
  const getTreeCode = (): string | null => {
    if (!selectedCategoryGroupCode || !categoryGroupCode) return null;
    return `${categoryGroupCode}:${selectedCategoryGroupCode}`;
  };

  // 오른쪽 → 가운데: 지정한 인덱스(슬롭)에 상품 추가
  const handleDropFromRight = async (insertIndex: number, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneActive(false);
    setDropInsertIndex(null);

    if (!selectedCategoryGroupCode) {
      alert('카테고리/그룹을 먼저 선택해 주세요.');
      return;
    }
    if (!draggedProduct || draggedIndex !== null || !projectDetail) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;

    const currentProducts = categoryGroupProducts.get(selectedCategoryGroupCode) || [];
    const treeCode = getTreeCode();
    if (!treeCode) return;

    if (currentProducts.some(cp => cp.productCode === draggedProduct.code)) {
      alert('이미 추가된 상품입니다.');
      return;
    }

    const prev = insertIndex > 0 ? currentProducts[insertIndex - 1] : null;
    const next = insertIndex < currentProducts.length ? currentProducts[insertIndex] : null;
    const sortOrder = generateBetween(prev?.sortOrder ?? null, next?.sortOrder ?? null);

    try {
      setApiBusy(true);
      await updateProductInTree(projectCode, draggedProduct.code, {
        operation: 'update',
        treeCode,
        sortOrder
      });
      const newEntry: CategoryGroupProduct = {
        categoryGroupCode: selectedCategoryGroupCode,
        productCode: draggedProduct.code,
        order: insertIndex,
        sortOrder
      };
      const newProducts = [...currentProducts];
      newProducts.splice(insertIndex, 0, newEntry);
      const withOrder = newProducts.map((cp, index) => ({ ...cp, order: index }));
      setCategoryGroupProducts(prev => new Map(prev).set(selectedCategoryGroupCode, withOrder));
    } catch (err) {
      console.error('상품 추가 실패:', err);
      alert(err instanceof Error ? err.message : '상품 추가에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  // 빈 가운데 영역에 드롭 (오른쪽에서 추가, insertIndex 0)
  const handleDropToCategoryGroup = (e: React.DragEvent) => {
    e.preventDefault();
    setDropZoneActive(false);
    setDropInsertIndex(null);
    if (draggedIndex === null && draggedProduct) {
      handleDropFromRight(0, e);
    }
  };

  // 삽입 슬롭에 드롭 (오른쪽에서 추가 or 가운데 내부 순서 변경)
  const handleDropOnSlot = (insertIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneActive(false);
    setDropInsertIndex(null);
    if (draggedIndex === null) {
      handleDropFromRight(insertIndex, e);
    } else {
      handleDropReorder(insertIndex)(e);
    }
  };

  // 가운데 영역에서 순서 변경 — 슬롭(삽입 위치)에 드롭
  const handleDropReorder = (slotIndex: number) => async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropInsertIndex(null);

    if (!selectedCategoryGroupCode || draggedIndex === null || !projectDetail) return;
    if (slotIndex === draggedIndex) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode || !draggedProduct) return;

    const treeCode = getTreeCode();
    if (!treeCode) return;

    const currentProducts = [...(categoryGroupProducts.get(selectedCategoryGroupCode) || [])];
    const [removed] = currentProducts.splice(draggedIndex, 1);
    const insertIndex = slotIndex > draggedIndex ? slotIndex - 1 : slotIndex;
    currentProducts.splice(insertIndex, 0, removed);

    const prev = insertIndex > 0 ? currentProducts[insertIndex - 1] : null;
    const next = insertIndex < currentProducts.length - 1 ? currentProducts[insertIndex + 1] : null;
    const sortOrder = generateBetween(prev?.sortOrder ?? null, next?.sortOrder ?? null);

    try {
      setApiBusy(true);
      await updateProductInTree(projectCode, removed.productCode, {
        operation: 'update',
        treeCode,
        sortOrder
      });
      const reordered = currentProducts.map((cp, index) => ({
        ...cp,
        order: index,
        sortOrder: cp.productCode === removed.productCode ? sortOrder : cp.sortOrder
      }));
      setCategoryGroupProducts(prevMap => new Map(prevMap).set(selectedCategoryGroupCode, reordered));
    } catch (err) {
      console.error('순서 변경 실패:', err);
      alert(err instanceof Error ? err.message : '순서 변경에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  // 카테고리/그룹에서 상품 제거 (즉시 API 호출)
  const handleRemoveProduct = async (productCode: string) => {
    if (!selectedCategoryGroupCode || !projectDetail) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;

    const treeCode = getTreeCode();
    if (!treeCode) return;

    try {
      setApiBusy(true);
      await updateProductInTree(projectCode, productCode, { operation: 'remove', treeCode });
      const currentProducts = (categoryGroupProducts.get(selectedCategoryGroupCode) || [])
        .filter(cp => cp.productCode !== productCode)
        .map((cp, index) => ({ ...cp, order: index }));
      setCategoryGroupProducts(prev => new Map(prev).set(selectedCategoryGroupCode, currentProducts));
    } catch (err) {
      console.error('상품 제거 실패:', err);
      alert(err instanceof Error ? err.message : '상품 제거에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  // 상품 카드 컴포넌트
  const ProductCard: React.FC<{
    product: Product;
    isDraggable?: boolean;
    showRemove?: boolean;
    onRemove?: () => void;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnd?: (e: React.DragEvent) => void;
  }> = ({ product, isDraggable = true, showRemove = false, onRemove, onDragStart, onDragEnd }) => {
    return (
      <div
        draggable={isDraggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className="group bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-indigo-300 hover:shadow-md transition-all cursor-move"
      >
        <div className="flex items-center gap-3">
          {isDraggable && (
            <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="w-6 h-6 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{product.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 font-mono">{product.itemCode}</span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs font-medium text-gray-700">{formatPrice(product.salePrice)}</span>
            </div>
          </div>
          {showRemove && onRemove && (
            <button
              onClick={onRemove}
              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // 카테고리/그룹 트리 아이템 렌더링
  const renderCategoryGroupTreeItem = (node: CategoryGroupNode, level: number = 0): React.ReactNode => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedCodes.has(node.code);
    const isSelected = selectedCategoryGroupCode === node.code;

    return (
      <div key={node.code}>
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
            isSelected ? 'bg-indigo-100 text-indigo-900 font-medium' : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
          onClick={() => handleCategoryGroupSelect(node.code)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryGroupToggle(node.code);
              }}
              className="p-0.5"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          {!hasChildren && <div className="w-5" />}
          <FolderTree className="w-4 h-4" />
          <span className="text-sm">{node.title}</span>
          {node.code && (
            <span className="text-xs text-gray-400">{node.code}</span>
          )}
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            상품: {!treeCountsLoaded ? '-' : (treeNodeProductCounts[`${categoryGroupCode}:${node.code}`] ?? 0)}
          </span>
          {node.type && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
              {node.type === 'CATEGORY' ? '카테고리' : '그룹'}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => renderCategoryGroupTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // 가격 포맷팅
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  // 현재 카테고리/그룹의 상품 목록 (sortOrder 기준 정렬)
  const currentCategoryGroupProducts = selectedCategoryGroupCode
    ? (categoryGroupProducts.get(selectedCategoryGroupCode) || [])
        .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
        .map(cp => allProducts.find(p => p.code === cp.productCode))
        .filter((p): p is Product => p !== undefined)
    : [];

  // 프로젝트 정보가 없을 때 처리
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="카테고리/그룹 상품 관리"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* 컨텐츠 영역 */}
        <main className="flex-1 flex flex-col min-h-0 p-4 sm:p-6">
          {/* 카테고리/그룹 정보 및 변경사항 관리 영역 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 flex-shrink-0">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                {selectedCategoryGroup && (
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                      <FolderTree className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{selectedCategoryGroup.title}</h3>
                      <p className="text-xs text-gray-500">{selectedCategoryGroup.brandName ? `${selectedCategoryGroup.brandName} (${selectedCategoryGroup.code})` : selectedCategoryGroup.code}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/products/category-groups')}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>목록으로</span>
                </button>
              </div>
            </div>
            {apiBusy && (
              <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                저장 중…
              </div>
            )}
          </div>

          {/* 3단 레이아웃 컨텐츠 영역 */}
          <div className="flex-1 min-h-0 grid grid-cols-12 gap-4">
            {/* 왼쪽: 카테고리/그룹 트리 */}
            <div className="col-span-3 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">카테고리/그룹</h3>
                <p className="text-xs text-gray-500 mt-1">
                  하위 카테고리/그룹
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {isLoadingTree ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : treeData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <FolderTree className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">하위 카테고리/그룹이 없습니다.</p>
                  </div>
                ) : (
                  treeData.map((node) => renderCategoryGroupTreeItem(node))
                )}
              </div>
            </div>

            {/* 가운데: 선택된 카테고리/그룹의 상품 */}
            <div className="col-span-5 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  {selectedCategoryGroupCode
                    ? `카테고리/그룹 상품 (${currentCategoryGroupProducts.length})`
                    : '카테고리/그룹을 선택하세요'}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  드래그하여 순서 변경 또는 오른쪽에서 추가
                </p>
              </div>
              <div
                className={`flex-1 overflow-y-auto p-4 space-y-2 ${
                  dropZoneActive ? 'bg-indigo-50 border-4 border-dashed border-indigo-300' : ''
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropZoneActive(true);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains((e.relatedTarget as Node) || null)) {
                    setDropInsertIndex(null);
                    setDropZoneActive(false);
                  }
                }}
                onDrop={handleDropToCategoryGroup}
              >
                {selectedCategoryGroupCode ? (
                  isLoadingNodeProducts ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  ) : currentCategoryGroupProducts.length > 0 ? (
                    <div className="flex flex-col gap-0">
                      {currentCategoryGroupProducts.map((product, index) => (
                        <React.Fragment key={product.code}>
                          {/* 삽입 슬롭: 맨 위·아이템 사이·맨 아래 */}
                          <div
                            className={`min-h-[12px] flex-shrink-0 rounded transition-colors ${
                              dropInsertIndex === index
                                ? 'bg-indigo-200 border-2 border-indigo-400 border-dashed'
                                : 'hover:bg-indigo-50/50'
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDropInsertIndex(index);
                              setDropZoneActive(true);
                            }}
                            onDrop={handleDropOnSlot(index)}
                          />
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDropInsertIndex(index);
                              setDropZoneActive(true);
                            }}
                            onDrop={handleDropOnSlot(index)}
                          >
                            <ProductCard
                              product={product}
                              showRemove
                              onRemove={() => handleRemoveProduct(product.code)}
                              onDragStart={handleDragStartFromCategory(product, index)}
                              onDragEnd={handleDragEnd}
                            />
                          </div>
                        </React.Fragment>
                      ))}
                      {/* 맨 끝 삽입 슬롭 */}
                      <div
                        className={`min-h-[12px] flex-shrink-0 rounded transition-colors ${
                          dropInsertIndex === currentCategoryGroupProducts.length
                            ? 'bg-indigo-200 border-2 border-indigo-400 border-dashed'
                            : 'hover:bg-indigo-50/50'
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDropInsertIndex(currentCategoryGroupProducts.length);
                          setDropZoneActive(true);
                        }}
                        onDrop={handleDropOnSlot(currentCategoryGroupProducts.length)}
                      />
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center py-20"
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDropZoneActive(true);
                      }}
                      onDragLeave={() => setDropZoneActive(false)}
                      onDrop={handleDropToCategoryGroup}
                    >
                      <Package className="w-16 h-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 text-center px-4">
                        오른쪽에서 상품을 드래그하여 추가하세요
                      </p>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center py-20">
                    <FolderTree className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">카테고리/그룹을 선택하세요</p>
                  </div>
                )}
              </div>
            </div>

            {/* 오른쪽: 전체 상품 */}
            <div className="col-span-4 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  전체 상품 ({allProducts.length})
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  드래그하여 카테고리/그룹에 추가
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {isLoadingProducts ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : productListError ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <p className="text-red-600 text-sm mb-4 text-center">{productListError}</p>
                    <button
                      onClick={() => {
                        setProductListError(null);
                        // 다시 로드
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      다시 시도
                    </button>
                  </div>
                ) : allProducts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Package className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">
                      상품이 없습니다.
                    </p>
                  </div>
                ) : (
                  allProducts.map((product) => (
                    <ProductCard
                      key={product.code}
                      product={product}
                      onDragStart={handleDragStartFromAll(product)}
                      onDragEnd={handleDragEnd}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CategoryGroupProductConfig;
